import React, { useState, useEffect, useCallback, useRef } from 'react';
import { visaAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './VisaManagement.css';

const VisaManagement = () => {
  // Active Tab
  const [activeTab, setActiveTab] = useState('configs');

  // Master Data
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [processingTypes, setProcessingTypes] = useState([]);

  // Configs Tab State
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  // Pricing Tab State
  const [pricing, setPricing] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);

  // Documents Tab State
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Filter Dropdowns
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const countryDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Status Dropdown in Table
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const statusDropdownRefs = useRef({});

  // Modal State
  const [modal, setModal] = useState({
    isOpen: false,
    type: '',
    mode: '',
    data: null
  });

  // Save error & loading
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalConfigs: 0,
    activeConfigs: 0,
    totalPricing: 0,
    totalDocuments: 0
  });

  // Fetch Master Data
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [countriesRes, categoriesRes, docTypesRes, procTypesRes] = await Promise.all([
          visaAPI.getCountries(),
          visaAPI.getCategories(),
          visaAPI.getDocumentTypes(),
          visaAPI.getProcessingTypes()
        ]);

        if (countriesRes.data.success) setCountries(countriesRes.data.data.countries);
        if (categoriesRes.data.success) setCategories(categoriesRes.data.data.categories);
        if (docTypesRes.data.success) setDocumentTypes(docTypesRes.data.data.documentTypes);
        if (procTypesRes.data.success) setProcessingTypes(procTypesRes.data.data.processingTypes);
      } catch (error) {
        console.error('Failed to fetch master data:', error);
      }
    };

    fetchMasterData();
  }, []);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
      if (statusDropdownOpen !== null) {
        const currentRef = statusDropdownRefs.current[statusDropdownOpen];
        if (currentRef && !currentRef.contains(event.target)) {
          setStatusDropdownOpen(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  // Fetch Configs
  const fetchConfigs = useCallback(async () => {
    try {
      setConfigsLoading(true);
      const params = {};
      if (selectedCountry) params.destination_country_id = selectedCountry.id;
      if (selectedCategory) params.visa_category_id = selectedCategory.id;

      const response = await visaAPI.getConfigs(params);
      if (response.data.success) {
        setConfigs(response.data.data.configs);
        const activeCount = response.data.data.configs.filter(c => c.is_active).length;
        setStats(prev => ({
          ...prev,
          totalConfigs: response.data.data.configs.length,
          activeConfigs: activeCount
        }));
      }
    } catch (error) {
      console.error('Failed to fetch configs:', error);
    } finally {
      setConfigsLoading(false);
    }
  }, [selectedCountry, selectedCategory]);

  useEffect(() => {
    if (activeTab === 'configs') {
      fetchConfigs();
    }
  }, [activeTab, fetchConfigs]);

  // Fetch Pricing
  const fetchPricing = useCallback(async () => {
    if (!selectedConfig) return;

    try {
      setPricingLoading(true);
      const response = await visaAPI.getPricing({ visa_config_id: selectedConfig.id });
      if (response.data.success) {
        setPricing(response.data.data.pricing);
        setStats(prev => ({ ...prev, totalPricing: response.data.data.pricing.length }));
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  }, [selectedConfig]);

  useEffect(() => {
    if (activeTab === 'pricing' && selectedConfig) {
      fetchPricing();
    }
  }, [activeTab, selectedConfig, fetchPricing]);

  // Fetch Documents
  const fetchDocuments = useCallback(async () => {
    if (!selectedConfig) return;

    try {
      setDocumentsLoading(true);
      const response = await visaAPI.getDocuments({ visa_config_id: selectedConfig.id });
      if (response.data.success) {
        setDocuments(response.data.data.documents);
        setStats(prev => ({ ...prev, totalDocuments: response.data.data.documents.length }));
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  }, [selectedConfig]);

  useEffect(() => {
    if (activeTab === 'documents' && selectedConfig) {
      fetchDocuments();
    }
  }, [activeTab, selectedConfig, fetchDocuments]);

  // Handlers
  const handleSelectConfig = (config) => {
    setSelectedConfig(config);
    setActiveTab('pricing');
  };

  const handleStatusChange = async (configId, newStatus) => {
    try {
      await visaAPI.updateConfig(configId, { is_active: newStatus });
      fetchConfigs();
      setStatusDropdownOpen(null);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleToggleFeatured = async (configId) => {
    try {
      await visaAPI.toggleFeatured(configId);
      fetchConfigs();
    } catch (error) {
      console.error('Failed to toggle featured:', error);
    }
  };

  const openModal = (type, mode, data = null) => {
    setModal({ isOpen: true, type, mode, data });
  };

  const closeModal = () => {
    setModal({ isOpen: false, type: '', mode: '', data: null });
    setSaveError('');
    setSaving(false);
  };

  const handleDeleteConfig = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visa configuration? This will soft-delete it.')) return;
    try {
      await visaAPI.deleteConfig(id);
      fetchConfigs();
      if (selectedConfig?.id === id) {
        setSelectedConfig(null);
        setActiveTab('configs');
      }
    } catch (error) {
      console.error('Failed to delete config:', error);
      alert(error.response?.data?.message || 'Failed to delete configuration');
    }
  };

  const handleSaveConfig = async (formData) => {
    try {
      setSaveError('');
      setSaving(true);
      if (modal.mode === 'create') {
        await visaAPI.createConfig(formData);
      } else {
        await visaAPI.updateConfig(modal.data.id, formData);
      }
      fetchConfigs();
      closeModal();
    } catch (error) {
      console.error('Failed to save config:', error);
      setSaveError(error.response?.data?.message || 'Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePricing = async (formData) => {
    try {
      setSaveError('');
      setSaving(true);
      if (modal.mode === 'create') {
        await visaAPI.createPricing({
          ...formData,
          visa_config_id: selectedConfig.id
        });
      } else {
        await visaAPI.updatePricing(modal.data.id, formData);
      }
      fetchPricing();
      closeModal();
    } catch (error) {
      console.error('Failed to save pricing:', error);
      setSaveError(error.response?.data?.message || 'Failed to save pricing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePricing = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pricing?')) return;
    try {
      await visaAPI.deletePricing(id);
      fetchPricing();
    } catch (error) {
      console.error('Failed to delete pricing:', error);
    }
  };

  const handleSaveDocument = async (formData) => {
    try {
      setSaveError('');
      setSaving(true);
      if (modal.mode === 'create') {
        await visaAPI.createDocument({
          ...formData,
          visa_config_id: selectedConfig.id
        });
      } else {
        await visaAPI.updateDocument(modal.data.id, formData);
      }
      fetchDocuments();
      closeModal();
    } catch (error) {
      console.error('Failed to save document:', error);
      setSaveError(error.response?.data?.message || 'Failed to save document. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document requirement?')) return;
    try {
      await visaAPI.deleteDocument(id);
      fetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setCountryDropdownOpen(false);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCategoryDropdownOpen(false);
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getEntriesBadgeClass = (entries) => {
    switch (entries) {
      case 'single': return 'entries-single';
      case 'double': return 'entries-double';
      case 'multiple': return 'entries-multiple';
      default: return '';
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">VISA MANAGEMENT</h1>
            <p className="page-subtitle">CONFIGURE VISA PRICING & DOCUMENTS</p>
          </div>
          <div className="header-right">
            {activeTab === 'configs' && (
              <>
                <div className="custom-dropdown" ref={countryDropdownRef}>
                  <button
                    className="custom-dropdown-trigger"
                    onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                  >
                    <span>{selectedCountry ? selectedCountry.name : 'All Countries'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {countryDropdownOpen && (
                    <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                      <div
                        className={`custom-dropdown-item ${!selectedCountry ? 'active' : ''}`}
                        onClick={() => handleCountrySelect(null)}
                      >
                        All Countries
                      </div>
                      {countries.map((country) => (
                        <div
                          key={country.id}
                          className={`custom-dropdown-item ${selectedCountry?.id === country.id ? 'active' : ''}`}
                          onClick={() => handleCountrySelect(country)}
                        >
                          {country.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="custom-dropdown" ref={categoryDropdownRef}>
                  <button
                    className="custom-dropdown-trigger"
                    onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                  >
                    <span>{selectedCategory?.name || 'All Categories'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {categoryDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      <div
                        className={`custom-dropdown-item ${!selectedCategory ? 'active' : ''}`}
                        onClick={() => handleCategorySelect(null)}
                      >
                        All Categories
                      </div>
                      {categories.map((category) => (
                        <div
                          key={category.id}
                          className={`custom-dropdown-item ${selectedCategory?.id === category.id ? 'active' : ''}`}
                          onClick={() => handleCategorySelect(category)}
                        >
                          {category.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            <button className="btn-primary" onClick={() => openModal(activeTab === 'configs' ? 'config' : activeTab === 'pricing' ? 'pricing' : 'document', 'create')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {activeTab === 'configs' ? 'ADD CONFIG' : activeTab === 'pricing' ? 'ADD PRICING' : 'ADD DOCUMENT'}
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid stats-grid-4">
            <div
              className={`stat-card stat-card-clickable ${activeTab === 'configs' ? 'stat-card-active' : ''}`}
              onClick={() => setActiveTab('configs')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL CONFIGS</span>
                <span className="stat-value">{stats.totalConfigs}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${activeTab === 'configs' && selectedCountry ? 'stat-card-active' : ''}`}
              onClick={() => setActiveTab('configs')}
            >
              <div className="stat-info">
                <span className="stat-label">ACTIVE CONFIGS</span>
                <span className="stat-value">{stats.activeConfigs}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-primary stat-card-clickable ${activeTab === 'pricing' ? 'stat-card-active' : ''}`}
              onClick={() => selectedConfig && setActiveTab('pricing')}
            >
              <div className="stat-info">
                <span className="stat-label">PRICING ENTRIES</span>
                <span className="stat-value">{stats.totalPricing}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-warning stat-card-clickable ${activeTab === 'documents' ? 'stat-card-active' : ''}`}
              onClick={() => selectedConfig && setActiveTab('documents')}
            >
              <div className="stat-info">
                <span className="stat-label">DOCUMENT TYPES</span>
                <span className="stat-value">{stats.totalDocuments}</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="tabs-container">
            <div className="tabs-nav">
              <button
                className={`tab-btn ${activeTab === 'configs' ? 'active' : ''}`}
                onClick={() => setActiveTab('configs')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                  <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
                VISA CONFIGURATIONS
              </button>
              <button
                className={`tab-btn ${activeTab === 'pricing' ? 'active' : ''}`}
                onClick={() => setActiveTab('pricing')}
                disabled={!selectedConfig}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                PRICING MATRIX
              </button>
              <button
                className={`tab-btn ${activeTab === 'documents' ? 'active' : ''}`}
                onClick={() => setActiveTab('documents')}
                disabled={!selectedConfig}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                DOCUMENTS
              </button>
            </div>
          </div>

          {/* Selected Config Info */}
          {selectedConfig && activeTab !== 'configs' && (
            <div className="selected-config-banner">
              <div className="config-banner-info">
                <span className="config-banner-label">Managing:</span>
                <span className="config-banner-name">
                  {selectedConfig.destination_country_name} - {selectedConfig.visa_name}
                </span>
              </div>
              <button className="btn-change-config" onClick={() => { setSelectedConfig(null); setActiveTab('configs'); }}>
                Change Config
              </button>
            </div>
          )}

          {/* Configs Tab */}
          {activeTab === 'configs' && (
            <div className="dashboard-section visa-table-section">
              {configsLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading configurations...</p>
                </div>
              ) : configs.length === 0 ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                    <line x1="2" y1="10" x2="22" y2="10"></line>
                  </svg>
                  <p>No visa configurations found</p>
                  <button className="btn-primary" onClick={() => openModal('config', 'create')}>
                    Add First Configuration
                  </button>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>DESTINATION</th>
                        <th>VISA TYPE</th>
                        <th>VALIDITY</th>
                        <th>ENTRIES</th>
                        <th>FEATURED</th>
                        <th>STATUS</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configs.map((config) => (
                        <tr key={config.id}>
                          <td>
                            <div className="destination-info">
                              <div className="destination-details">
                                <span className="destination-name">{config.destination_country_name}</span>
                                <span className="destination-category">{config.visa_category_name}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="visa-type-info">
                              <span className="visa-name">{config.visa_name}</span>
                            </div>
                          </td>
                          <td>
                            <div className="validity-info">
                              <span className="validity-period">
                                {config.validity_value ? `${config.validity_value} ${config.validity_unit}` : '-'}
                              </span>
                              {config.max_stay_value && (
                                <span className="max-stay">Max: {config.max_stay_value} {config.max_stay_unit}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`entries-badge ${getEntriesBadgeClass(config.entries_allowed)}`}>
                              {config.entries_allowed?.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <button
                              className={`featured-toggle ${Number(config.is_featured) === 1 ? 'featured-active' : ''}`}
                              onClick={() => handleToggleFeatured(config.id)}
                              title={Number(config.is_featured) === 1 ? 'Remove from featured' : 'Add to featured'}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill={Number(config.is_featured) === 1 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                              </svg>
                            </button>
                          </td>
                          <td>
                            <div className="status-cell">
                              <div
                                className="status-custom-dropdown"
                                ref={el => statusDropdownRefs.current[config.id] = el}
                              >
                                <button
                                  className={`status-dropdown-trigger status-dropdown-${config.is_active ? 'approved' : 'rejected'}`}
                                  onClick={(e) => {
                                    if (statusDropdownOpen === config.id) {
                                      setStatusDropdownOpen(null);
                                    } else {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setDropdownPosition({
                                        top: rect.bottom + 4,
                                        left: rect.left
                                      });
                                      setStatusDropdownOpen(config.id);
                                    }
                                  }}
                                >
                                  <span>{config.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                {statusDropdownOpen === config.id && (
                                  <div
                                    className="status-dropdown-menu"
                                    style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                                  >
                                    <div
                                      className={`status-dropdown-item ${config.is_active ? 'active' : ''}`}
                                      onClick={() => handleStatusChange(config.id, true)}
                                    >
                                      ACTIVE
                                    </div>
                                    <div
                                      className={`status-dropdown-item ${!config.is_active ? 'active' : ''}`}
                                      onClick={() => handleStatusChange(config.id, false)}
                                    >
                                      INACTIVE
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-action btn-manage"
                                onClick={() => handleSelectConfig(config)}
                              >
                                MANAGE
                              </button>
                              <button
                                className="btn-action btn-edit"
                                onClick={() => openModal('config', 'edit', config)}
                              >
                                EDIT
                              </button>
                              <button
                                className="btn-action btn-delete"
                                onClick={() => handleDeleteConfig(config.id)}
                              >
                                DELETE
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && selectedConfig && (
            <div className="dashboard-section visa-table-section">
              <div className="section-header">
                <h3>Pricing Matrix</h3>
                <button className="btn-primary" onClick={() => openModal('pricing', 'create')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  ADD PRICING
                </button>
              </div>
              {pricingLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading pricing...</p>
                </div>
              ) : pricing.length === 0 ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <p>No pricing configured for this visa</p>
                  <button className="btn-primary" onClick={() => openModal('pricing', 'create')}>
                    Add First Pricing
                  </button>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>NATIONALITY</th>
                        <th>PROCESSING TYPE</th>
                        <th>EMBASSY FEES</th>
                        <th>NAYTIVE SERVICE FEES</th>
                        <th>PAY AT EMBASSY</th>
                        <th>TOTAL (PAY NOW)</th>
                        <th>PROCESSING TIME</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricing.map((price) => {
                        const payAtEmbassy = parseFloat(price.vfs_fee) || 0;
                        const total = (parseFloat(price.government_fee) || 0) +
                                      (parseFloat(price.service_fee) || 0);
                        return (
                          <tr key={price.id}>
                            <td>
                              <span className="nationality-name">{price.nationality_name || 'All Nationalities'}</span>
                            </td>
                            <td>
                              <span className={`processing-badge processing-${price.processing_type_name?.toLowerCase().replace(/\s+/g, '-')}`}>
                                {price.processing_type_name}
                              </span>
                            </td>
                            <td>
                              <span className="fee-amount">${parseFloat(price.government_fee || 0).toFixed(2)}</span>
                            </td>
                            <td>
                              <span className="fee-amount">${parseFloat(price.service_fee || 0).toFixed(2)}</span>
                            </td>
                            <td>
                              {payAtEmbassy > 0
                                ? <span className="fee-amount">${payAtEmbassy.toFixed(2)}</span>
                                : <span style={{ color: 'var(--text-muted, #aaa)' }}>—</span>}
                            </td>
                            <td>
                              <span className="fee-amount fee-total">${total.toFixed(2)}</span>
                            </td>
                            <td>
                              <span className="processing-time">
                                {price.processing_days_min && price.processing_days_max
                                  ? `${price.processing_days_min}-${price.processing_days_max} days`
                                  : price.processing_days_min
                                    ? `${price.processing_days_min}+ days`
                                    : '-'}
                              </span>
                            </td>
                            <td>
                              <div className="actions-cell">
                                <button
                                  className="btn-action btn-edit"
                                  onClick={() => openModal('pricing', 'edit', price)}
                                >
                                  EDIT
                                </button>
                                <button
                                  className="btn-action btn-delete"
                                  onClick={() => handleDeletePricing(price.id)}
                                >
                                  DELETE
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && selectedConfig && (
            <div className="dashboard-section visa-table-section">
              {documentsLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading documents...</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                  <p>No document requirements configured</p>
                  <button className="btn-primary" onClick={() => openModal('document', 'create')}>
                    Add First Document
                  </button>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>DOCUMENT</th>
                        <th>NATIONALITY</th>
                        <th>REQUIRED</th>
                        <th>SAMPLE</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => (
                        <tr key={doc.id}>
                          <td>
                            <span className="document-name">{doc.document_type_name}</span>
                          </td>
                          <td>
                            <span className="nationality-text">
                              {doc.nationality_name || 'All Nationalities'}
                            </span>
                          </td>
                          <td>
                            <span className={`mandatory-badge ${doc.is_mandatory ? 'required' : 'optional'}`}>
                              {doc.is_mandatory ? 'REQUIRED' : 'OPTIONAL'}
                            </span>
                          </td>
                          <td>
                            <label className="sample-upload-btn">
                              <input
                                type="file"
                                accept=".html"
                                style={{ display: 'none' }}
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (!file) return;
                                  try {
                                    await visaAPI.uploadSampleDocument(doc.id, file);
                                    alert('Sample uploaded');
                                    fetchDocuments();
                                  } catch (err) {
                                    alert('Upload failed');
                                  }
                                }}
                              />
                              {doc.sample_document_key ? '✓ Replace' : '+ Upload'}
                            </label>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-action btn-edit"
                                onClick={() => openModal('document', 'edit', doc)}
                              >
                                EDIT
                              </button>
                              <button
                                className="btn-action btn-delete"
                                onClick={() => handleDeleteDocument(doc.id)}
                              >
                                DELETE
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Config Modal */}
        {modal.isOpen && modal.type === 'config' && (
          <ConfigModal
            mode={modal.mode}
            data={modal.data}
            countries={countries}
            categories={categories}
            onSave={handleSaveConfig}
            onClose={closeModal}
            saveError={saveError}
            saving={saving}
          />
        )}

        {/* Pricing Modal */}
        {modal.isOpen && modal.type === 'pricing' && (
          <PricingModal
            mode={modal.mode}
            data={modal.data}
            countries={countries}
            processingTypes={processingTypes}
            onSave={handleSavePricing}
            onClose={closeModal}
            saveError={saveError}
            saving={saving}
          />
        )}

        {/* Document Modal */}
        {modal.isOpen && modal.type === 'document' && (
          <DocumentModal
            mode={modal.mode}
            data={modal.data}
            countries={countries}
            documentTypes={documentTypes}
            onSave={handleSaveDocument}
            onClose={closeModal}
            saveError={saveError}
            saving={saving}
            onDocTypeCreated={(newDoc) => setDocumentTypes(prev => [...prev, newDoc].sort((a, b) => a.name.localeCompare(b.name)))}
            onDocTypeUpdated={(updated) => setDocumentTypes(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d).sort((a, b) => a.name.localeCompare(b.name)))}
            onDocTypeDeleted={(id) => setDocumentTypes(prev => prev.filter(d => d.id !== id))}
          />
        )}
        {/* AI Config Chat */}
        <AiConfigChat onActionComplete={() => {
          fetchConfigs();
          if (selectedConfig) {
            fetchPricing(selectedConfig.id);
            fetchDocuments(selectedConfig.id);
          }
        }} />
      </main>
    </div>
  );
};

// Options Picker (for nationality selection etc.)
const OptionsPicker = ({ options, onSelect, disabled, multiSelect = false }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const allOption = options.find(o => o.value === 'all');
  const regularOptions = options.filter(o => o.value !== 'all');
  const filtered = search.trim()
    ? regularOptions.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : regularOptions;

  const handleClick = (opt) => {
    if (multiSelect) {
      setSelected(prev => {
        const exists = prev.find(s => s.value === opt.value);
        if (exists) return prev.filter(s => s.value !== opt.value);
        return [...prev, opt];
      });
    } else {
      onSelect(opt);
    }
  };

  const handleSubmitMulti = () => {
    if (selected.length === 0) return;
    const combined = {
      label: selected.map(s => s.label).join(', '),
      value: selected.map(s => s.value).join(',')
    };
    onSelect(combined);
  };

  const isSelected = (opt) => selected.some(s => s.value === opt.value);

  return (
    <div className="ai-chat-options-picker">
      {allOption && (
        <button
          className="ai-chat-option-btn ai-chat-option-all"
          onClick={() => onSelect(allOption)}
          disabled={disabled}
        >
          {allOption.label}
        </button>
      )}
      {regularOptions.length > 10 && (
        <input
          type="text"
          className="ai-chat-options-search"
          placeholder={multiSelect ? "Search documents..." : "Search country..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      {multiSelect && selected.length > 0 && (
        <div className="ai-chat-selected-tags">
          {selected.map((s, k) => (
            <span key={k} className="ai-chat-tag">
              {s.label}
              <button onClick={() => setSelected(prev => prev.filter(x => x.value !== s.value))}>x</button>
            </span>
          ))}
        </div>
      )}
      <div className="ai-chat-options-list">
        {filtered.map((opt, k) => (
          <button
            key={k}
            className={`ai-chat-option-btn ${isSelected(opt) ? 'ai-chat-option-selected' : ''}`}
            onClick={() => handleClick(opt)}
            disabled={disabled}
          >
            {opt.label}
          </button>
        ))}
        {filtered.length === 0 && search && (
          <span className="ai-chat-options-empty">No match found</span>
        )}
      </div>
      {multiSelect && (
        <button
          className="ai-chat-option-btn ai-chat-option-all"
          onClick={handleSubmitMulti}
          disabled={disabled || selected.length === 0}
        >
          {selected.length === 0 ? 'Select documents above' : `Submit ${selected.length} document${selected.length > 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  );
};

// AI Config Chat Component
const AiConfigChat = ({ onActionComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hey! I can help you manage visa configs. Just tell me what you need and I\'ll walk you through it.\n\nTry something like:\n- "Create a tourist visa for UK"\n- "Add pricing to Thailand tourist visa"\n- "Edit service fee for UAE business visa"\n- "Delete Japan transit visa"' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !loading) inputRef.current?.focus();
  }, [messages, loading, isOpen]);

  const processResponse = (data) => {
    if (!data.success) {
      return { content: `Error: ${data.message}` };
    }
    let text = data.message || '';
    if (data.results && data.results.length > 0) {
      const resultLines = data.results.map(r =>
        `${r.success ? '\u2705' : '\u274c'} ${r.message}`
      ).join('\n');
      text += (text ? '\n\n' : '') + resultLines;
    }
    return {
      content: text || 'Done!',
      options: data.options || null,
      executed: data.executed
    };
  };

  const sendMessage = async (text) => {
    setLoading(true);
    try {
      // Build conversation history (skip welcome message)
      const history = messages.slice(1).map(m => ({ role: m.role, content: m.content }));
      const res = await visaAPI.aiConfig(text, history);
      const { content, options, executed } = processResponse(res.data);

      const msgObj = { role: 'assistant', content };
      if (options) msgObj.options = options;
      if (res.data.optionsType) msgObj.optionsType = res.data.optionsType;
      setMessages(prev => [...prev, msgObj]);

      if (executed && res.data.results?.some(r => r.success)) {
        onActionComplete();
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err.response?.data?.message || err.message}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    await sendMessage(msg);
  };

  const handleOptionClick = async (option) => {
    if (loading) return;
    setMessages(prev => [...prev, { role: 'user', content: option.label }]);
    // For multi-select (docs), send label + IDs so LLM can map them
    const msgText = option.value && option.value.includes(',')
      ? `${option.label} [IDs: ${option.value}]`
      : option.label;
    await sendMessage(msgText);
  };

  return (
    <>
      <button
        className={`ai-chat-fab ${isOpen ? 'ai-chat-fab-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="AI Config Builder"
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"></path>
            <line x1="9" y1="21" x2="15" y2="21"></line>
            <line x1="10" y1="24" x2="14" y2="24"></line>
          </svg>
        )}
      </button>

      {isOpen && (
        <div className="ai-chat-panel">
          <div className="ai-chat-header">
            <div className="ai-chat-header-info">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"></path>
              </svg>
              <span>AI Config Builder</span>
            </div>
            <button className="ai-chat-close" onClick={() => setIsOpen(false)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="ai-chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`ai-chat-msg ai-chat-msg-${msg.role}`}>
                <div className="ai-chat-msg-bubble" dangerouslySetInnerHTML={{
                  __html: msg.content
                    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n/g, '<br />')
                }} />
                {/* Clickable option buttons */}
                {msg.options && msg.options.length > 0 && i === messages.length - 1 && (
                  <OptionsPicker options={msg.options} onSelect={handleOptionClick} disabled={loading} multiSelect={msg.optionsType === 'documents'} />
                )}
              </div>
            ))}
            {loading && (
              <div className="ai-chat-msg ai-chat-msg-assistant">
                <div className="ai-chat-msg-bubble ai-chat-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-chat-input-area">
            <input
              ref={inputRef}
              type="text"
              className="ai-chat-input"
              placeholder="e.g. Create tourist visa for Thailand..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              disabled={loading}
            />
            <button
              className="ai-chat-send"
              onClick={handleSend}
              disabled={loading || !input.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Config Modal Component
const ConfigModal = ({ mode, data, countries, categories, onSave, onClose, saveError, saving }) => {
  const [formData, setFormData] = useState({
    destination_country_id: data?.destination_country_id || '',
    visa_category_id: data?.visa_category_id || '',
    visa_name: data?.visa_name || '',
    validity_value: data?.validity_value || '',
    validity_unit: data?.validity_unit || 'years',
    max_stay_value: data?.max_stay_value || '',
    max_stay_unit: data?.max_stay_unit || 'days',
    entries_allowed: data?.entries_allowed || 'multiple',
    notes: data?.notes || '',
    application_website_url: data?.application_website_url || '',
    is_active: data?.is_active ?? true
  });

  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [validityUnitDropdownOpen, setValidityUnitDropdownOpen] = useState(false);
  const [maxStayUnitDropdownOpen, setMaxStayUnitDropdownOpen] = useState(false);
  const [entriesDropdownOpen, setEntriesDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const countryRef = useRef(null);
  const categoryRef = useRef(null);
  const validityUnitRef = useRef(null);
  const maxStayUnitRef = useRef(null);
  const entriesRef = useRef(null);
  const statusRef = useRef(null);
  const countrySearchRef = useRef(null);

  const selectedCountry = countries.find(c => c.id === parseInt(formData.destination_country_id));
  const selectedCategory = categories.find(c => c.id === parseInt(formData.visa_category_id));

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const unitOptions = [
    { value: 'days', label: 'Days' },
    { value: 'months', label: 'Months' },
    { value: 'years', label: 'Years' }
  ];

  const entriesOptions = [
    { value: 'single', label: 'Single' },
    { value: 'double', label: 'Double' },
    { value: 'multiple', label: 'Multiple' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
        setCountrySearch('');
      }
      if (categoryRef.current && !categoryRef.current.contains(event.target)) setCategoryDropdownOpen(false);
      if (validityUnitRef.current && !validityUnitRef.current.contains(event.target)) setValidityUnitDropdownOpen(false);
      if (maxStayUnitRef.current && !maxStayUnitRef.current.contains(event.target)) setMaxStayUnitDropdownOpen(false);
      if (entriesRef.current && !entriesRef.current.contains(event.target)) setEntriesDropdownOpen(false);
      if (statusRef.current && !statusRef.current.contains(event.target)) setStatusDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus search input when country dropdown opens
  useEffect(() => {
    if (countryDropdownOpen && countrySearchRef.current) {
      countrySearchRef.current.focus();
    }
  }, [countryDropdownOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-form" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'create' ? 'Add Visa Configuration' : 'Edit Visa Configuration'}
          </h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Destination Country *</label>
                <div className="custom-dropdown" ref={countryRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}>
                    <span>{selectedCountry ? selectedCountry.name : 'Select Country'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {countryDropdownOpen && (
                    <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                      <div className="dropdown-search">
                        <input
                          ref={countrySearchRef}
                          type="text"
                          placeholder="Search countries..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {filteredCountries.length === 0 ? (
                        <div className="dropdown-no-results">No countries found</div>
                      ) : (
                        filteredCountries.map((country) => (
                          <div key={country.id} className={`custom-dropdown-item ${formData.destination_country_id === country.id ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, destination_country_id: country.id })); setCountryDropdownOpen(false); setCountrySearch(''); }}>
                            {country.name}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Visa Category *</label>
                <div className="custom-dropdown" ref={categoryRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}>
                    <span>{selectedCategory?.name || 'Select Category'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {categoryDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      {categories.map((category) => (
                        <div key={category.id} className={`custom-dropdown-item ${formData.visa_category_id === category.id ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, visa_category_id: category.id })); setCategoryDropdownOpen(false); }}>
                          {category.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group full-width">
              <label>Visa Name *</label>
              <input type="text" value={formData.visa_name} onChange={(e) => setFormData(prev => ({ ...prev, visa_name: e.target.value }))} placeholder="e.g., B1/B2 Tourist Visa, Schengen Tourist" required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Validity Period</label>
                <div className="input-with-unit">
                  <input type="number" min="0" value={formData.validity_value} onChange={(e) => setFormData(prev => ({ ...prev, validity_value: e.target.value }))} placeholder="e.g., 10" />
                  <div className="custom-dropdown unit-dropdown" ref={validityUnitRef}>
                    <button type="button" className="custom-dropdown-trigger" onClick={() => setValidityUnitDropdownOpen(!validityUnitDropdownOpen)}>
                      <span>{unitOptions.find(u => u.value === formData.validity_unit)?.label}</span>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    {validityUnitDropdownOpen && (
                      <div className="custom-dropdown-menu">
                        {unitOptions.map((option) => (
                          <div key={option.value} className={`custom-dropdown-item ${formData.validity_unit === option.value ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, validity_unit: option.value })); setValidityUnitDropdownOpen(false); }}>
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Max Stay</label>
                <div className="input-with-unit">
                  <input type="number" min="0" value={formData.max_stay_value} onChange={(e) => setFormData(prev => ({ ...prev, max_stay_value: e.target.value }))} placeholder="e.g., 180" />
                  <div className="custom-dropdown unit-dropdown" ref={maxStayUnitRef}>
                    <button type="button" className="custom-dropdown-trigger" onClick={() => setMaxStayUnitDropdownOpen(!maxStayUnitDropdownOpen)}>
                      <span>{unitOptions.find(u => u.value === formData.max_stay_unit)?.label}</span>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    {maxStayUnitDropdownOpen && (
                      <div className="custom-dropdown-menu">
                        {unitOptions.map((option) => (
                          <div key={option.value} className={`custom-dropdown-item ${formData.max_stay_unit === option.value ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, max_stay_unit: option.value })); setMaxStayUnitDropdownOpen(false); }}>
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Entries Allowed</label>
                <div className="custom-dropdown" ref={entriesRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setEntriesDropdownOpen(!entriesDropdownOpen)}>
                    <span>{entriesOptions.find(e => e.value === formData.entries_allowed)?.label || 'Select'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {entriesDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      {entriesOptions.map((option) => (
                        <div key={option.value} className={`custom-dropdown-item ${formData.entries_allowed === option.value ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, entries_allowed: option.value })); setEntriesDropdownOpen(false); }}>
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Status</label>
                <div className="custom-dropdown" ref={statusRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}>
                    <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {statusDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      <div className={`custom-dropdown-item ${formData.is_active ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, is_active: true })); setStatusDropdownOpen(false); }}>Active</div>
                      <div className={`custom-dropdown-item ${!formData.is_active ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, is_active: false })); setStatusDropdownOpen(false); }}>Inactive</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group full-width">
              <label>Application Website URL</label>
              <input type="url" value={formData.application_website_url} onChange={(e) => setFormData(prev => ({ ...prev, application_website_url: e.target.value }))} placeholder="https://www.vfsglobal.com/..." />
            </div>
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} rows={3} placeholder="Additional notes about this visa type..." />
            </div>
          </div>
          {saveError && (
            <div className="modal-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {saveError}
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Pricing Modal Component
const PricingModal = ({ mode, data, countries, processingTypes, onSave, onClose, saveError, saving }) => {
  // Format date to YYYY-MM-DD for date input
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return new Date().toISOString().split('T')[0];
    const date = new Date(dateValue);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    nationality_country_id: data?.nationality_country_id || '',
    processing_type_id: data?.processing_type_id || '',
    government_fee: data?.government_fee || 0,
    service_fee: data?.service_fee || 0,
    vfs_fee: data?.vfs_fee != null && data?.vfs_fee !== 0 ? data.vfs_fee : '',
    processing_days_min: data?.processing_days_min || '',
    processing_days_max: data?.processing_days_max || '',
    effective_from: formatDateForInput(data?.effective_from)
  });

  const [nationalityDropdownOpen, setNationalityDropdownOpen] = useState(false);
  const [processingDropdownOpen, setProcessingDropdownOpen] = useState(false);
  const [nationalitySearch, setNationalitySearch] = useState('');
  const nationalityRef = useRef(null);
  const processingRef = useRef(null);
  const nationalitySearchRef = useRef(null);

  const selectedNationality = countries.find(c => c.id === parseInt(formData.nationality_country_id));
  const selectedProcessing = processingTypes.find(p => p.id === parseInt(formData.processing_type_id));

  const filteredNationalities = countries.filter(country =>
    country.name.toLowerCase().includes(nationalitySearch.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nationalityRef.current && !nationalityRef.current.contains(event.target)) {
        setNationalityDropdownOpen(false);
        setNationalitySearch('');
      }
      if (processingRef.current && !processingRef.current.contains(event.target)) setProcessingDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus search input when nationality dropdown opens
  useEffect(() => {
    if (nationalityDropdownOpen && nationalitySearchRef.current) {
      nationalitySearchRef.current.focus();
    }
  }, [nationalityDropdownOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, vfs_fee: formData.vfs_fee === '' ? 0 : parseFloat(formData.vfs_fee) || 0 });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-form" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Pricing' : 'Edit Pricing'}</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Nationality *</label>
                <div className="custom-dropdown" ref={nationalityRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setNationalityDropdownOpen(!nationalityDropdownOpen)}>
                    <span>{formData.nationality_country_id === '' ? 'All Nationalities' : (selectedNationality ? selectedNationality.name : 'Select Nationality')}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {nationalityDropdownOpen && (
                    <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                      <div className="dropdown-search">
                        <input
                          ref={nationalitySearchRef}
                          type="text"
                          placeholder="Search countries..."
                          value={nationalitySearch}
                          onChange={(e) => setNationalitySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className={`custom-dropdown-item ${formData.nationality_country_id === '' ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, nationality_country_id: '' })); setNationalityDropdownOpen(false); setNationalitySearch(''); }}>All Nationalities</div>
                      {filteredNationalities.length === 0 ? (
                        <div className="dropdown-no-results">No countries found</div>
                      ) : (
                        filteredNationalities.map((country) => (
                          <div key={country.id} className={`custom-dropdown-item ${formData.nationality_country_id === country.id ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, nationality_country_id: country.id })); setNationalityDropdownOpen(false); setNationalitySearch(''); }}>
                            {country.name}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Processing Type *</label>
                <div className="custom-dropdown" ref={processingRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setProcessingDropdownOpen(!processingDropdownOpen)}>
                    <span>{selectedProcessing?.name || 'Select Processing'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {processingDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      {processingTypes.map((type) => (
                        <div key={type.id} className={`custom-dropdown-item ${formData.processing_type_id === type.id ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, processing_type_id: type.id })); setProcessingDropdownOpen(false); }}>
                          {type.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Embassy Fees (USD) *</label>
                <input type="number" step="0.01" min="0" value={formData.government_fee} onChange={(e) => setFormData(prev => ({ ...prev, government_fee: parseFloat(e.target.value) || 0 }))} required />
              </div>
              <div className="form-group">
                <label>Naytive Service Fees (USD) *</label>
                <input type="number" step="0.01" min="0" value={formData.service_fee} onChange={(e) => setFormData(prev => ({ ...prev, service_fee: parseFloat(e.target.value) || 0 }))} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Pay at Embassy (USD) <span style={{ fontWeight: 400, color: 'var(--text-muted, #888)', fontSize: '0.85em' }}>— optional</span></label>
                <input type="number" step="0.01" min="0" placeholder="Leave blank if not applicable" value={formData.vfs_fee} onChange={(e) => setFormData(prev => ({ ...prev, vfs_fee: e.target.value === '' ? '' : parseFloat(e.target.value) }))} />
              </div>
            </div>
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Min Days *</label>
                <input type="number" min="0" value={formData.processing_days_min} onChange={(e) => setFormData(prev => ({ ...prev, processing_days_min: parseInt(e.target.value) || '' }))} required />
              </div>
              <div className="form-group">
                <label>Max Days *</label>
                <input type="number" min="0" value={formData.processing_days_max} onChange={(e) => setFormData(prev => ({ ...prev, processing_days_max: parseInt(e.target.value) || '' }))} required />
              </div>
              <div className="form-group">
                <label>Effective From *</label>
                <input type="date" value={formData.effective_from} onChange={(e) => setFormData(prev => ({ ...prev, effective_from: e.target.value }))} required />
              </div>
            </div>
          </div>
          {saveError && (
            <div className="modal-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {saveError}
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Document Modal Component
const DocumentModal = ({ mode, data, countries, documentTypes, onSave, onClose, saveError, saving, onDocTypeCreated, onDocTypeUpdated, onDocTypeDeleted }) => {
  const [formData, setFormData] = useState({
    nationality_country_id: data?.nationality_country_id || '',
    document_type_id: data?.document_type_id || '',
    is_mandatory: data?.is_mandatory ?? true,
    special_instructions: data?.special_instructions || '',
    sample_document_url: data?.sample_document_url || ''
  });

  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [nationalityDropdownOpen, setNationalityDropdownOpen] = useState(false);
  const [mandatoryDropdownOpen, setMandatoryDropdownOpen] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [nationalitySearch, setNationalitySearch] = useState('');
  const [creatingDocType, setCreatingDocType] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [newDocTypeName, setNewDocTypeName] = useState('');
  const [editingDocType, setEditingDocType] = useState(null);
  const [editDocTypeName, setEditDocTypeName] = useState('');
  const documentRef = useRef(null);
  const nationalityRef = useRef(null);
  const mandatoryRef = useRef(null);
  const documentSearchRef = useRef(null);
  const nationalitySearchRef = useRef(null);
  const createPopupRef = useRef(null);

  const selectedDocument = documentTypes.find(d => d.id === parseInt(formData.document_type_id));
  const selectedNationality = countries.find(c => c.id === parseInt(formData.nationality_country_id));

  const filteredDocumentTypes = documentTypes.filter(doc =>
    doc.name.toLowerCase().includes(documentSearch.toLowerCase())
  );
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(nationalitySearch.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (documentRef.current && !documentRef.current.contains(event.target)) {
        setDocumentDropdownOpen(false);
        setDocumentSearch('');
      }
      if (nationalityRef.current && !nationalityRef.current.contains(event.target)) {
        setNationalityDropdownOpen(false);
        setNationalitySearch('');
      }
      if (mandatoryRef.current && !mandatoryRef.current.contains(event.target)) setMandatoryDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (documentDropdownOpen && documentSearchRef.current) {
      documentSearchRef.current.focus();
    }
  }, [documentDropdownOpen]);

  useEffect(() => {
    if (nationalityDropdownOpen && nationalitySearchRef.current) {
      nationalitySearchRef.current.focus();
    }
  }, [nationalityDropdownOpen]);

  const handleOpenCreatePopup = () => {
    setNewDocTypeName(documentSearch.trim());
    setShowCreatePopup(true);
    setDocumentDropdownOpen(false);
  };

  const handleCreateDocType = async () => {
    if (!newDocTypeName.trim()) return;
    setCreatingDocType(true);
    try {
      const res = await visaAPI.createDocumentType(newDocTypeName.trim());
      if (res.data.success) {
        const newDoc = res.data.data;
        if (onDocTypeCreated) onDocTypeCreated(newDoc);
        setFormData(prev => ({ ...prev, document_type_id: newDoc.id }));
        setShowCreatePopup(false);
        setDocumentSearch('');
        setNewDocTypeName('');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create document type');
    } finally {
      setCreatingDocType(false);
    }
  };

  const handleEditDocType = (e, doc) => {
    e.stopPropagation();
    setEditingDocType(doc);
    setEditDocTypeName(doc.name);
    setShowCreatePopup(true);
    setDocumentDropdownOpen(false);
  };

  const handleUpdateDocType = async () => {
    if (!editDocTypeName.trim() || !editingDocType) return;
    setCreatingDocType(true);
    try {
      const res = await visaAPI.updateDocumentType(editingDocType.id, editDocTypeName.trim());
      if (res.data.success) {
        if (onDocTypeUpdated) onDocTypeUpdated(res.data.data);
        setShowCreatePopup(false);
        setEditingDocType(null);
        setEditDocTypeName('');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update document type');
    } finally {
      setCreatingDocType(false);
    }
  };

  const handleDeleteDocType = async (e, doc) => {
    e.stopPropagation();
    if (!window.confirm(`Delete document type "${doc.name}"? This cannot be undone.`)) return;
    try {
      const res = await visaAPI.deleteDocumentType(doc.id);
      if (res.data.success) {
        if (onDocTypeDeleted) onDocTypeDeleted(doc.id);
        if (parseInt(formData.document_type_id) === doc.id) {
          setFormData(prev => ({ ...prev, document_type_id: '' }));
        }
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete document type');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-form" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Document Requirement' : 'Edit Document Requirement'}</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Document Type *</label>
                <div className="custom-dropdown" ref={documentRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setDocumentDropdownOpen(!documentDropdownOpen)}>
                    <span>{selectedDocument?.name || 'Select Document'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {documentDropdownOpen && (
                    <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                      <div className="dropdown-search">
                        <input
                          ref={documentSearchRef}
                          type="text"
                          placeholder="Search or type new name..."
                          value={documentSearch}
                          onChange={(e) => setDocumentSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {filteredDocumentTypes.map((doc) => (
                        <div key={doc.id} className={`custom-dropdown-item doctype-item ${formData.document_type_id === doc.id ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, document_type_id: doc.id })); setDocumentDropdownOpen(false); setDocumentSearch(''); }}>
                          <span className="doctype-name">{doc.name}</span>
                          <span className="doctype-actions">
                            <button type="button" className="doctype-action-btn doctype-edit-btn" title="Edit" onClick={(e) => handleEditDocType(e, doc)}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            <button type="button" className="doctype-action-btn doctype-delete-btn" title="Delete" onClick={(e) => handleDeleteDocType(e, doc)}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                          </span>
                        </div>
                      ))}
                      {filteredDocumentTypes.length === 0 && !documentSearch.trim() && (
                        <div className="custom-dropdown-item disabled">No documents found</div>
                      )}
                      <div className="dropdown-divider"></div>
                      <div className="custom-dropdown-item create-new-item" onClick={handleOpenCreatePopup}>
                        + Create New{documentSearch.trim() ? ` "${documentSearch.trim()}"` : ''}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Nationality</label>
                <div className="custom-dropdown" ref={nationalityRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setNationalityDropdownOpen(!nationalityDropdownOpen)}>
                    <span>{selectedNationality ? selectedNationality.name : 'All Nationalities'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {nationalityDropdownOpen && (
                    <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                      <div className="dropdown-search">
                        <input
                          ref={nationalitySearchRef}
                          type="text"
                          placeholder="Search nationality..."
                          value={nationalitySearch}
                          onChange={(e) => setNationalitySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {!nationalitySearch && (
                        <div className={`custom-dropdown-item ${!formData.nationality_country_id ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, nationality_country_id: '' })); setNationalityDropdownOpen(false); setNationalitySearch(''); }}>All Nationalities</div>
                      )}
                      {filteredCountries.map((country) => (
                        <div key={country.id} className={`custom-dropdown-item ${formData.nationality_country_id === country.id ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, nationality_country_id: country.id })); setNationalityDropdownOpen(false); setNationalitySearch(''); }}>
                          {country.name}
                        </div>
                      ))}
                      {filteredCountries.length === 0 && nationalitySearch && (
                        <div className="custom-dropdown-item disabled">No countries found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Mandatory</label>
                <div className="custom-dropdown" ref={mandatoryRef}>
                  <button type="button" className="custom-dropdown-trigger full-width" onClick={() => setMandatoryDropdownOpen(!mandatoryDropdownOpen)}>
                    <span>{formData.is_mandatory ? 'Required' : 'Optional'}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  {mandatoryDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      <div className={`custom-dropdown-item ${formData.is_mandatory ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, is_mandatory: true })); setMandatoryDropdownOpen(false); }}>Required</div>
                      <div className={`custom-dropdown-item ${!formData.is_mandatory ? 'active' : ''}`} onClick={() => { setFormData(prev => ({ ...prev, is_mandatory: false })); setMandatoryDropdownOpen(false); }}>Optional</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="form-group full-width">
              <label>Special Instructions</label>
              <textarea value={formData.special_instructions} onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))} rows={3} placeholder="Specific instructions for this document..." />
            </div>
          </div>
          {saveError && (
            <div className="modal-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {saveError}
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}</button>
          </div>
        </form>
      </div>

      {/* Create/Edit Document Type Popup */}
      {showCreatePopup && (
        <div className="create-doctype-overlay" onClick={() => { setShowCreatePopup(false); setEditingDocType(null); setEditDocTypeName(''); setNewDocTypeName(''); }}>
          <div className="create-doctype-popup" onClick={(e) => e.stopPropagation()}>
            <h3>{editingDocType ? 'Edit Document Type' : 'Create New Document Type'}</h3>
            <div className="form-group">
              <label>Document Type Name *</label>
              <input
                type="text"
                value={editingDocType ? editDocTypeName : newDocTypeName}
                onChange={(e) => editingDocType ? setEditDocTypeName(e.target.value) : setNewDocTypeName(e.target.value)}
                placeholder="Enter document type name..."
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); editingDocType ? handleUpdateDocType() : handleCreateDocType(); } }}
              />
            </div>
            <div className="create-doctype-actions">
              <button type="button" className="btn-cancel" onClick={() => { setShowCreatePopup(false); setEditingDocType(null); setEditDocTypeName(''); setNewDocTypeName(''); }}>Cancel</button>
              <button type="button" className="btn-save" onClick={editingDocType ? handleUpdateDocType : handleCreateDocType} disabled={creatingDocType || !(editingDocType ? editDocTypeName.trim() : newDocTypeName.trim())}>
                {creatingDocType ? 'Saving...' : editingDocType ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisaManagement;
